<template>
  <div>
    <div v-if="selected">
      <strong>原始設定:</strong>
      <dl>
        <dt>教室</dt>
        <dd>
          {{ selected.roomName }}
        </dd>
        <dt>不可借用星期</dt>
        <dd>
          {{ weekdays.filter(w => selected.weekdays.some(rw => rw === w.key)).map(w => w.label).join(', ') }}
        </dd>
        <dt>不可借用節次</dt>
        <dd>
          {{ `[${selected.periodBegin}-${selected.periodEnd}]` }}
        </dd>
        <dt>不可借用時間</dt>
        <dd>
          {{ `${selected.beginDate} ~ ${selected.endDate}` }}
        </dd>
        <dt>事由</dt>
        <dd>
          {{ selected.reason }}
        </dd>
      </dl>
    </div>
    <el-divider v-if="selected">分隔線</el-divider>
    <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-width="120px"
    >
      <el-form-item label="借用星期" prop="weekdays">
        <el-select
            v-model="form.weekdays"
            clearable
            multiple
            placeholder="請選擇星期(多選)"
        >
          <el-option
              v-for="w in weekdays"
              :key="w.key"
              :label="w.label"
              :value="w.key"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="借用節次(起訖)" prop="periods">
        <period-picker
            v-model="form.periods"
            :comparer="(b, e) => e >= b"
            :disabled="form.weekdays.length === 0"
            :options="periodOpts"
            clearable
            @beginChange="form.periods[1] = ''"
        ></period-picker>
      </el-form-item>
      <el-form-item label="日期起訖" prop="range">
        <el-date-picker
            v-model="form.range"
            :disabled="form.sameAsSemesterRange || pickerDisabled"
            :picker-options="datePickerOptions"
            end-placeholder="請選擇結束日期"
            range-separator="到"
            start-placeholder="請選擇開始日期"
            type="daterange"
            value-format="yyyy-MM-dd"
        ></el-date-picker>
        <el-checkbox
            v-model="form.sameAsSemesterRange"
            :disabled="pickerDisabled"
            style="margin-left: 5px;"
            @change="onSameAsSemesterRangeChange"
        >
          全學期
        </el-checkbox>
      </el-form-item>
      <el-form-item v-if="withRoom" label="教室" prop="roomIds">
        <el-select
            v-model="form.roomIds"
            :disabled="!form.range || form.range.length < 2"
            filterable
            multiple
            placeholder="請選擇教室(多選)"
        >
          <el-option
              v-for="room in vacanciesLocal"
              :key="room.id"
              :label="room.name + (room.isHidden ? '（已隱藏）' : '')"
              :value="room.id"
          >
          </el-option>
        </el-select>
        <el-button
            :disabled="!form.range || form.range.length < 2"
            style="margin-left: 5px;"
            type="info"
            @click="onSelectAll"
        >
          全選
        </el-button>
        <div style="color: red;">※已選擇{{ form.roomIds.length }}間教室</div>
      </el-form-item>
      <el-form-item
          label="事由"
          prop="reason"
      >
        <el-input
            v-model="form.reason"
            placeholder="請輸入事由(選填)"
            rows="5"
            type="textarea"
        ></el-input>
      </el-form-item>
      <slot></slot>
    </el-form>
  </div>
</template>

<script>
import PeriodPicker from '../PeriodPicker';
import UnavailableMixin from './UnavailableMixin';
import moment from 'moment';

const defaultForm = {
  weekdays: [],
  periods: [],
  range: undefined,
  roomIds: [],
  sameAsSemesterRange: false,
  reason: null,
};

export default {
  name: 'UnavailableForm',
  data: () => ({
    form: {...defaultForm},
    vacanciesLocal: []
  }),
  components: {PeriodPicker},
  mixins: [UnavailableMixin],
  computed: {
    rules() {
      const {range, periods, roomIds, weekdays} = {
        range: [
          {required: true, message: '請選擇日期起訖', min: 2, max: 2, type: 'array', trigger: 'change'}
        ],
        periods: [
          {
            required: true,
            message: '請選擇節次起訖',
            validator: (rule, value, cb) => {
              if (!value || value.length < 2 || value.some(v => !v)) {
                cb(new Error('請選擇節次起訖'));
              }
              cb();
            }
          }
        ],
        roomIds: [
          {required: true, message: '請選擇教室', type: 'array', trigger: 'change'}
        ],
        weekdays: [
          {required: true, message: '請選擇星期', type: 'array', trigger: 'change'}
        ],
      };
      if (this.withRoom) {
        return {range, periods, roomIds, weekdays};
      }

      return {range, periods, weekdays};
    },

    rangeForWatch() {
      return this.form.range;
    },
    periodsForWatch() {
      return this.form.periods;
    },
    weekdaysForWatch() {
      return this.form.weekdays;
    },

  },

  props: {
    withRoom: {
      type: Boolean,
      default: true
    },
    selected: {
      type: Object,
      default: () => undefined
    }
  },

  methods: {
    reset() {
      this.$refs.form.clearValidate();
      this.resetForm();
      this.$refs.form.resetFields();
    },

    resetForm() {
      this.form = {...defaultForm};
    },

    onSelectAll() {
      this.form.roomIds = this.vacanciesLocal.map(r => r.id);
    },

    onSameAsSemesterRangeChange(check) {
      if (check) {
        const {dateBegin, dateEnd} = this.sys;
        const baseBegin = (this.weekRanges.find(w => w.week === 1) || {begin: dateBegin}).begin;
        const currentBegin = moment().format('YYYY-MM-DD');

        const end = (this.weekRanges.find(w => w.week === 18) || {end: dateEnd}).end;
        this.form.range = [
          currentBegin >= baseBegin ? currentBegin : baseBegin,
          end >= currentBegin ? end : currentBegin
        ];
      }
    },

    getRef() {
      return this.$refs.form;
    },

    getFields() {
      return this.form;
    },

    isSelectCollided() {
      if (!this.selected) return false;
      return this.vacanciesLocal.map(v => v.ncutRoomId).indexOf(this.selected.roomId) === -1;
    }
  },
  watch: {
    weekdaysForWatch(current) {
      if (!current || current.length === 0) {
        this.form.periods = [];
        this.form.roomIds = [];
        this.form.sameAsSemesterRange = false;
      }
      this.form.range = undefined;
    },

    periodsForWatch(current) {
      if (!current || current.some(c => !c)) {
        this.form.roomIds = [];
        this.form.sameAsSemesterRange = false;
      }
      this.form.range = undefined;
    },

    async rangeForWatch(current) {
      if (current && current.length === 2) {
        const {schoolYear, semester, form} = this;
        if(this.selected){
          await this.getVacancyRoomsInRangeExcept({
            schoolYear,
            semester,
            beginDate: form.range[0],
            endDate: form.range[1],
            begin: form.periods[0],
            end: form.periods[1],
            weekdays: form.weekdays,
            unavailableId: this.selected.id
          });
        } else {
          await this.getVacancyRoomsInRange({
            schoolYear,
            semester,
            beginDate: form.range[0],
            endDate: form.range[1],
            begin: form.periods[0],
            end: form.periods[1],
            weekdays: form.weekdays
          });
        }
        this.vacanciesLocal = [...this.vacancies];

        //update
        if (this.selected) {
          if (this.isSelectCollided()) {
            this.$message({
              type: 'warning',
              message: '您選的設定，目前有衝堂，請選擇別的設定'
            });
            return;
          }
        } else {
          if (this.vacanciesLocal.length === 0) {
            this.$message({
              type: 'warning',
              message: '您選的設定，目前查無可用教室，請重新設定'
            })
          }
        }

      } else {
        this.form.roomIds = [];
      }

    },
    async schoolYear(current) {
      const {semester} = this;
      await this.getWeekRanges({
        schoolYear: current,
        semester
      });
    }
  },

  async created() {
    if (this.schoolYear) {
      await this.getWeekRanges({
        schoolYear: this.schoolYear,
        semester: this.semester
      });
    }
  },
}
</script>

<style scoped>

</style>
